import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  VolumeUp as SoundOnIcon,
  VolumeOff as SoundOffIcon,
  Fullscreen as FullscreenIcon,
  NotificationsActive as AlertActiveIcon,
  Circle as StatusIcon,
} from '@mui/icons-material';
import { Alert as AlertType } from '../services/metricsApi';
import { incidentsApi } from '../services/incidentsApi';
import websocketService, { WebSocketEvent } from '../services/websocket';
import { RefreshIconButton } from '../components/RefreshIconButton';
import { buildScopedIncidentsPath } from '../utils/incidentsNavigation';
import { ensureUiScope } from '../services/api';
import {
  applyPersistedSnapshot,
  emailDeliveryBuyerCopy,
  isEngineeringOrTransientWs,
  newlyConfirmedIncidentIds,
  shouldPlayPersistedIncidentSound,
  PARTIAL_SOURCE_UNAVAILABLE_MESSAGE,
  type BuyerLiveItem,
  type LiveAlertsViewState,
} from '../domain/incidents/liveAlertsReconciliation';
import { presentAlertHistoryRow, groupRepeatedOpenHistory, splitHistoryByLocalDay, type AlertHistoryRow } from '../domain/incidents/alertHistoryPresentation';
import { presentCompositeAlertDetail, presentReviewedCompositePattern } from '../domain/incidents/compositeAlertPresentation';
import { CompositeAlertDetail, ReviewedCompositePatternDetail } from '../components/incidents/CompositeAlertDetail';
import { groupActiveAlertsByOperationalScope } from '../domain/incidents/activeAlertsPresentation';
import { camerasApi } from '../services/camerasApi';

interface LiveAlert extends AlertType {
  receivedAt: string;
  acknowledged: boolean;
}

const ACTIVE_ALERTS_STORAGE_KEY = 'edgentik.active_alerts.v2';

const toLiveAlert = (item: BuyerLiveItem): LiveAlert => {
  const op = item.operational || ({} as BuyerLiveItem['operational']);
  const severityObj = op.severity && typeof op.severity === 'object' ? op.severity : null;
  const severity = String(severityObj?.severity || (typeof op.severity === 'string' ? op.severity : '') || 'medium');
  const incidentType = String(op.incident_type || op.condition_type || 'incident');
  const zone = String(op.zone_id || op.area_id || 'unknown');
  const timestamp = String(op.recorded_at || op.updated_at || new Date().toISOString());
  const title = incidentType.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
  return {
    uid: op.incident_id,
    alert_id: op.incident_id,
    alert_type: incidentType,
    title,
    description: `${title} in ${zone}`,
    state: String(op.operational_status || '').toUpperCase() === 'RESOLVED' ? 'resolved' : 'firing',
    severity,
    zone,
    timestamp,
    receivedAt: timestamp,
    acknowledged: false,
    labels: {
      severity,
      zone,
      ...(op.policy_id ? { policy_id: op.policy_id } : {}),
      ...(severityObj?.policy_version ? { policy_version: severityObj.policy_version } : {}),
    },
    annotations: {
      description: `${title} in ${zone}`,
      evidence_present: op.evidence_present ? 'true' : 'false',
      operational_status: op.operational_status || 'OPEN',
      email_delivery_copy: emailDeliveryBuyerCopy(op.notification_delivery),
      email_delivery_state: op.notification_delivery?.state || 'NOT_CONFIGURED',
      email_delivery_configured: op.notification_delivery?.configured ? 'true' : 'false',
      alert_class: String(op.alert_class || ''),
      camera_id: op.camera_id || '',
    },
  };
};

function historyClassColor(alertClass: AlertHistoryRow['alertClass']): 'secondary' | 'info' | 'default' {
  if (alertClass === 'Pattern') return 'secondary';
  if (alertClass === 'Composite') return 'info';
  return 'default';
}

function renderHistoryRow(
  row: AlertHistoryRow,
  navigate: (path: string) => void,
  onSelectComposite: (incidentId: string) => void,
) {
  return (
    <TableRow key={row.incidentId} hover data-testid="alert-history-row">
      <TableCell>
        <Typography variant="caption">{row.timeLabel}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.conditionLabel}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{row.cameraAreaLabel}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={row.alertClass}
          size="small"
          color={historyClassColor(row.alertClass)}
          variant="outlined"
          data-testid="alert-history-alert-class"
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption">{row.severity}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">
          {row.status}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" data-testid="alert-history-delivery">
          {row.deliveryLabel}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">
          {row.ruleName || row.patternIdentity || row.patternMembershipSummary || (row.alertClass === 'Pattern' ? 'Pattern' : '—')}
        </Typography>
      </TableCell>
      <TableCell>
        {row.alertClass === 'Composite' || (row.alertClass === 'Pattern' && row.investigateHref.startsWith('/live?')) ? (
          <Button
            size="small"
            onClick={() => onSelectComposite(row.incidentId)}
            data-testid={row.alertClass === 'Pattern' ? 'alert-history-composite-review' : 'alert-history-composite-details'}
          >
            {row.alertClass === 'Pattern' ? 'Review' : 'Details'}
          </Button>
        ) : (
          <>
            <Button
              size="small"
              onClick={() => navigate(buildScopedIncidentsPath(row.investigateHref))}
              data-testid="alert-history-investigate"
            >
              Investigate
            </Button>
            {row.lifecycleHref && (
              <Button size="small" onClick={() => navigate(row.lifecycleHref!)}>
                Lifecycle
              </Button>
            )}
            {row.spatialHref && (
              <Button size="small" onClick={() => navigate(row.spatialHref!)}>
                Spatial
              </Button>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

const LiveMonitor: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeAlerts, setActiveAlerts] = useState<LiveAlert[]>([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [readState, setReadState] = useState<'loading' | 'ok' | 'empty' | 'unavailable' | 'partial'>('loading');
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [historyItems, setHistoryItems] = useState<BuyerLiveItem[]>([]);
  const [liveItems, setLiveItems] = useState<BuyerLiveItem[]>([]);
  const [historyState, setHistoryState] = useState<'loading' | 'ok' | 'empty' | 'unavailable'>('loading');
  const [cameraNameById, setCameraNameById] = useState<Record<string, string>>({});
  const [showEarlierHistory, setShowEarlierHistory] = useState(false);
  const [expandedHistoryKeys, setExpandedHistoryKeys] = useState<Record<string, boolean>>({});
  const [selectedCompositeId, setSelectedCompositeId] = useState<string | null>(null);
  const fetchGenerationRef = React.useRef(0);
  const viewRef = React.useRef<LiveAlertsViewState>({
    readState: 'loading',
    items: [],
    appliedGeneration: 0,
    appliedScopeKey: '',
  });

  useEffect(() => {
    const incidentId = String(searchParams.get('incident_id') || '').trim();
    if (incidentId) setSelectedCompositeId(incidentId);
  }, [searchParams]);

  // Restore last seen alerts across navigation (session-scoped).
  useEffect(() => {
    try {
      // One-time cleanup for legacy cache key.
      sessionStorage.removeItem('edgentik.active_alerts.v1');
      const raw = sessionStorage.getItem(ACTIVE_ALERTS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      const restored = parsed.filter((a: any) => a && typeof a === 'object');
      if (restored.length) setActiveAlerts(restored as LiveAlert[]);
    } catch {
      // best-effort
    }
  }, []);

  // Persist so the "Live Alerts" view doesn't reset to empty on navigation.
  useEffect(() => {
    try {
      sessionStorage.setItem(ACTIVE_ALERTS_STORAGE_KEY, JSON.stringify(activeAlerts || []));
    } catch {
      // best-effort
    }
  }, [activeAlerts]);

  const scopeKey = useMemo(() => {
    try {
      const scope = ensureUiScope();
      return `${scope.orgId || ''}::${scope.siteId || ''}`;
    } catch {
      return 'default';
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const scope = ensureUiScope();
        const siteId = String(scope.siteId || '').trim();
        if (!siteId) return;
        const res = await camerasApi.listCameras(siteId);
        if (cancelled) return;
        const names: Record<string, string> = {};
        for (const camera of res.items || []) {
          const id = String(camera.camera_id || '').trim();
          const name = String(camera.name || '').trim();
          if (id && name) names[id] = name;
        }
        setCameraNameById(names);
      } catch {
        // Existing configured labels still apply when catalog is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scopeKey]);

  // Fetch authoritative persisted incident state.
  const fetchData = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    try {
      setLoading(true);
      const snapshot = await incidentsApi.getBuyerLiveAlerts({ limit: 200 });
      const next = applyPersistedSnapshot({
        generation,
        appliedGeneration: viewRef.current.appliedGeneration,
        scopeKey,
        appliedScopeKey: viewRef.current.appliedScopeKey,
        failed: snapshot.ok === false || snapshot.read_state === 'unavailable',
        response: snapshot,
        previous: viewRef.current,
      });
      viewRef.current = next;
      setReadState(next.readState);
      if (next.readState !== 'unavailable') {
        setLiveItems(next.items);
        setActiveAlerts((prev) => {
          const acked = new Set(
            (prev || []).filter((row) => row.acknowledged).map((row) => String(row.uid || row.alert_id || ''))
          );
          return next.items.map((item) => {
            const row = toLiveAlert(item);
            if (acked.has(String(row.uid || ''))) return { ...row, acknowledged: true };
            return row;
          });
        });
      }
    } catch (err) {
      console.error('Failed to fetch persisted live alerts:', err);
      const next = applyPersistedSnapshot({
        generation,
        appliedGeneration: viewRef.current.appliedGeneration,
        scopeKey,
        appliedScopeKey: viewRef.current.appliedScopeKey,
        failed: true,
        response: null,
        previous: viewRef.current,
      });
      viewRef.current = next;
      setReadState('unavailable');
    } finally {
      if (generation === fetchGenerationRef.current) setLoading(false);
      setLastFetchedAt(new Date());
    }
  }, [scopeKey]);

  const fetchHistory = useCallback(async () => {
    try {
      const snapshot = await incidentsApi.getBuyerAlertHistory();
      if (snapshot.ok === false || snapshot.read_state === 'unavailable') {
        setHistoryState('unavailable');
        return;
      }
      const items = snapshot.items || [];
      setHistoryItems(items);
      setHistoryState(items.length ? 'ok' : 'empty');
    } catch {
      setHistoryState('unavailable');
    }
  }, []);

  const historyRows = useMemo(
    () => (historyItems || [])
      .map((entry) => presentAlertHistoryRow(entry, { cameraNameById }))
      .filter((row): row is AlertHistoryRow => Boolean(row)),
    [historyItems, cameraNameById],
  );
  const historyWindow = useMemo(() => splitHistoryByLocalDay(historyRows), [historyRows]);
  const historyList = useMemo(() => {
    const visible = showEarlierHistory
      ? [...historyWindow.recent, ...historyWindow.earlier]
      : historyWindow.recent;
    return groupRepeatedOpenHistory(visible);
  }, [historyWindow, showEarlierHistory]);

  const activeCards = useMemo(
    () => groupActiveAlertsByOperationalScope(liveItems, { cameraNameById }),
    [liveItems, cameraNameById],
  );
  const ackedIncidentIds = useMemo(
    () => new Set(
      (activeAlerts || [])
        .filter((row) => row.acknowledged)
        .map((row) => String(row.uid || row.alert_id || '')),
    ),
    [activeAlerts],
  );
  const activeCardViews = useMemo(
    () => activeCards.map((card) => ({
      ...card,
      acknowledged: card.incidentIds.length > 0
        && card.incidentIds.every((id) => ackedIncidentIds.has(id)),
    })),
    [activeCards, ackedIncidentIds],
  );

  // WebSocket is invalidation only. Sound waits for persisted confirmation.
  const handleWebSocketEvent = useCallback((event: WebSocketEvent) => {
    if (event.type !== 'alert.new' && event.type !== 'alert.resolved') return;
    const payload = {
      incident_id: event.data?.incident_id || event.data?.alert_id,
      alert_id: event.data?.alert_id,
      transient: event.data?.transient === true,
      engineering_signal: event.data?.engineering_signal === true,
      not_incident_backed: event.data?.not_incident_backed === true,
    };
    if (event.type === 'alert.new' && isEngineeringOrTransientWs(payload)) {
      return;
    }
    const previousIds = (viewRef.current.items || []).map((item) => String(item.operational?.incident_id || ''));
    void (async () => {
      await fetchData();
      void fetchHistory();
      if (
        shouldPlayPersistedIncidentSound({
          soundEnabled,
          invalidationType: event.type,
          readState: viewRef.current.readState,
          confirmedNewIds: newlyConfirmedIncidentIds(previousIds, viewRef.current.items || []),
        })
      ) {
        playAlertSound();
      }
    })();
  }, [fetchData, fetchHistory, soundEnabled]);

  // Play alert sound
  const playAlertSound = () => {
    try {
      const audio = new Audio('/alert-sound.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Audio play failed (autoplay policy)
      });
    } catch (e) {
      // Audio not available
    }
  };

  // Initialize + refetch on reconnect.
  useEffect(() => {
    fetchData();
    void fetchHistory();
    websocketService.connect();
    const unsubscribe = websocketService.on('*', handleWebSocketEvent);
    let seenDisconnect = false;
    const unsubscribeConnection = websocketService.onConnectionChange((connected) => {
      setWsConnected(connected);
      if (!connected) {
        seenDisconnect = true;
        return;
      }
      if (seenDisconnect) {
        void fetchData();
        void fetchHistory();
      }
    });
    const checkConnection = setInterval(() => {
      setWsConnected(websocketService.isConnected());
    }, 2000);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      if (typeof unsubscribeConnection === 'function') unsubscribeConnection();
      clearInterval(checkConnection);
    };
  }, [fetchData, fetchHistory, handleWebSocketEvent]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Acknowledge alert
  const handleAcknowledge = (incidentIds: string[]) => {
    const ids = new Set(incidentIds.map((id) => String(id || '').trim()).filter(Boolean));
    setActiveAlerts((prev) =>
      prev.map((a) =>
        ids.has(String(a.uid || '')) || ids.has(String(a.alert_id || ''))
          ? { ...a, acknowledged: true }
          : a
      )
    );
    for (const alert of activeAlerts) {
      const deviceId = alert.device_id || alert.labels?.device_id;
      const uid = String(alert.uid || '');
      if (deviceId && ids.has(uid)) {
        websocketService.acknowledgeAlert(uid, deviceId);
      }
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'info';
    }
  };


  const criticalCount = activeCardViews.filter(
    (card) => String(card.severity || '').toLowerCase() === 'critical' && !card.acknowledged
  ).length;

  const alertSummary = useMemo(() => {
    const unacked = activeCardViews.filter((card) => !card.acknowledged);
    const sev = (card: (typeof activeCardViews)[number]) => String(card.severity || '').toLowerCase();
    const critical = unacked.filter((card) => sev(card) === 'critical').length;
    const high = unacked.filter((card) => sev(card) === 'high').length;
    const medium = unacked.filter((card) => sev(card) === 'medium').length;
    const low = unacked.filter((card) => sev(card) === 'low').length;
    const other = Math.max(0, unacked.length - critical - high - medium - low);
    let lastTs = 0;
    for (const card of activeCardViews) {
      const t = Date.parse(card.lastActivityAt || '') || 0;
      if (t > lastTs) lastTs = t;
    }
    return {
      unacked: unacked.length,
      total: activeCardViews.length,
      critical,
      high,
      medium,
      low,
      other,
      lastEventLabel: lastTs ? new Date(lastTs).toLocaleString() : '—',
    };
  }, [activeCardViews]);

  const selectedCompositeDetail = useMemo(() => {
    if (!selectedCompositeId) return null;
    const match = (historyItems || []).concat(liveItems || []).find(
      (entry) => String(entry?.operational?.incident_id || '') === selectedCompositeId,
    );
    return presentCompositeAlertDetail(match || null);
  }, [historyItems, liveItems, selectedCompositeId]);

  const selectedReviewedComposite = useMemo(() => {
    if (!selectedCompositeId) return null;
    const pool = (historyItems || []).concat(liveItems || []);
    const match = pool.find(
      (entry) => String(entry?.operational?.incident_id || '') === selectedCompositeId,
    );
    const ids = match?.operational?.pattern_membership?.source_occurrence_ids || [];
    const members = pool.filter((entry) => ids.includes(String(entry?.operational?.incident_id || '')));
    return presentReviewedCompositePattern(match || null, members);
  }, [historyItems, liveItems, selectedCompositeId]);

  const openCardInIncidents = useCallback((href: string, alertClass?: AlertHistoryRow['alertClass'], incidentId?: string) => {
    if (alertClass === 'Composite' && incidentId) {
      setSelectedCompositeId(incidentId);
      return;
    }
    navigate(buildScopedIncidentsPath(href));
  }, [navigate]);

  return (
    <Box sx={{ 
      p: 2, 
      height: 'calc(100vh - 64px)', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden', 
      width: '100%', 
      maxWidth: '100%',
      boxSizing: 'border-box' 
    }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{
          bgcolor: criticalCount > 0 ? 'error.main' : 'background.paper',
          p: 2,
          borderRadius: 1,
          color: criticalCount > 0 ? 'white' : 'inherit',
          flexShrink: 0,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box display="flex" alignItems="center">
          <AlertActiveIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography variant="h5" component="h1">
            Live alerts
          </Typography>
          {criticalCount > 0 && (
            <Chip
              label={`${criticalCount} CRITICAL`}
              color="error"
              sx={{ ml: 2, animation: 'pulse 1s infinite' }}
            />
          )}
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            icon={<StatusIcon sx={{ fontSize: 12 }} />}
            label={wsConnected ? 'LIVE' : 'OFFLINE'}
            color={wsConnected ? 'success' : 'default'}
            size="small"
          />
          <IconButton
            onClick={() => setSoundEnabled(!soundEnabled)}
            color={criticalCount > 0 ? 'inherit' : 'default'}
          >
            {soundEnabled ? <SoundOnIcon /> : <SoundOffIcon />}
          </IconButton>
          <RefreshIconButton
            onClick={() => {
              fetchData();
              void fetchHistory();
            }}
            loading={loading}
            color={criticalCount > 0 ? 'inherit' : 'default'}
            title="Refresh alerts"
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/live-view')}
            sx={
              criticalCount > 0
                ? { color: 'inherit', borderColor: 'rgba(255,255,255,0.65)' }
                : {}
            }
          >
            Open Live View
          </Button>
          <IconButton color={criticalCount > 0 ? 'inherit' : 'default'}>
            <FullscreenIcon />
          </IconButton>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, flexShrink: 0 }} />}

      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'hidden', 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        minHeight: 0,
      }}>
        <Paper
          sx={{
            p: 2,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
            boxSizing: 'border-box',
            minHeight: 0,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Active Alerts ({activeCardViews.filter((card) => !card.acknowledged).length})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Current open situations for this site, grouped by condition and camera/area.
            WebSocket events refresh the snapshot; they do not create cards on their own.
            Use <strong>Investigate</strong> for the latest matching incident.{' '}
            <strong>Open Live View</strong> is for camera streams.
          </Typography>
          {readState === 'unavailable' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Incident state is unavailable. This is not an empty queue. Showing last known rows when present.
            </Alert>
          )}
          {readState === 'partial' && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="buyer-live-partial-unavailable">
              {PARTIAL_SOURCE_UNAVAILABLE_MESSAGE}
            </Alert>
          )}
          {activeCardViews.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Chip size="small" label={`Unacked ${alertSummary.unacked}`} color="primary" variant="outlined" />
              {alertSummary.critical > 0 && (
                <Chip size="small" label={`Critical ${alertSummary.critical}`} color="error" />
              )}
              {alertSummary.high > 0 && (
                <Chip size="small" label={`High ${alertSummary.high}`} color="warning" />
              )}
              {(alertSummary.medium > 0 || alertSummary.low > 0 || alertSummary.other > 0) && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Other ${alertSummary.medium + alertSummary.low + alertSummary.other}`}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`Listed ${alertSummary.total}`}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: { xs: 0, sm: 1 } }}>
                Latest activity: {alertSummary.lastEventLabel}
              </Typography>
            </Box>
          )}
          <Divider sx={{ mb: 2 }} />
          
          {readState === 'unavailable' && activeCardViews.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
              <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Incident state unavailable
              </Typography>
              <Typography color="textSecondary">
                Persisted incident read failed. Retry instead of treating this as zero incidents.
              </Typography>
            </Box>
          ) : readState === 'partial' && activeCardViews.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
              <WarningIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Incident list may be incomplete
              </Typography>
              <Typography color="textSecondary">{PARTIAL_SOURCE_UNAVAILABLE_MESSAGE}</Typography>
            </Box>
          ) : activeCardViews.length === 0 && readState !== 'loading' ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
              <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No active incidents
              </Typography>
              <Typography color="textSecondary">
                No persisted open incidents for this site.
              </Typography>
            </Box>
          ) : activeCardViews.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
              <Typography color="textSecondary">Loading persisted incidents…</Typography>
            </Box>
          ) : (
            <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {activeCardViews.map((card) => (
                <Paper
                  key={card.key}
                  variant="outlined"
                  data-testid="buyer-live-active-alert-card"
                  sx={{
                    p: 1.5,
                    opacity: card.acknowledged ? 0.6 : 1,
                    bgcolor: card.acknowledged
                      ? 'action.hover'
                      : String(card.severity || '').toLowerCase() === 'critical'
                        ? 'error.light'
                        : undefined,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box>
                      <Typography variant="body1" fontWeight="bold" data-testid="buyer-live-condition">
                        {card.conditionLabel}
                      </Typography>
                      <Typography variant="body2" data-testid="buyer-live-camera-area">
                        {card.cameraAreaLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" data-testid="buyer-live-last-activity">
                        Last activity: {card.lastActivityLabel} · {card.status}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        icon={
                          String(card.severity || '').toLowerCase() === 'critical' ? (
                            <ErrorIcon />
                          ) : (
                            <WarningIcon />
                          )
                        }
                        label={card.severity || 'warning'}
                        color={getSeverityColor(card.severity) as any}
                        size="small"
                      />
                      <Chip
                        label={card.alertClass}
                        size="small"
                        color={historyClassColor(card.alertClass)}
                        variant="outlined"
                        data-testid="buyer-live-alert-class"
                      />
                    </Box>
                  </Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    data-testid="buyer-live-email-delivery"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    {card.deliveryCopy || emailDeliveryBuyerCopy(null)}
                  </Typography>
                  <Box display="flex" gap={1} mt={1.5}>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => openCardInIncidents(card.investigateHref, card.alertClass, card.representativeIncidentId)}
                      data-testid={card.alertClass === 'Composite' ? 'buyer-live-composite-details' : 'buyer-live-investigate'}
                    >
                      {card.alertClass === 'Composite' ? 'Details' : 'Investigate'}
                    </Button>
                    <Button
                      size="small"
                      variant={card.acknowledged ? 'text' : 'contained'}
                      color="primary"
                      onClick={() => handleAcknowledge(card.incidentIds)}
                      disabled={card.acknowledged}
                      data-testid="buyer-live-acknowledge"
                    >
                      {card.acknowledged ? 'ACK' : 'Acknowledge'}
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 2, mt: 2, flexShrink: 0 }} data-testid="alert-history-panel">
          <Typography variant="h6" gutterBottom>
            Alert History
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Active Alerts above is the open queue. History is today&apos;s persisted record, including
            closed incidents. Immediate, Pattern, and Composite are distinct classes.
            Immediate opens on a new operational incident; repeats are grouped and
            notification is limited by policy timing. Email Sent is never assumed from a queue or
            SMTP attempt.
          </Typography>
          {historyState === 'unavailable' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Alert History is unavailable. This is not an empty history.
            </Alert>
          )}
          {historyState === 'empty' && (
            <Typography color="text.secondary">No persisted Alerts for this site.</Typography>
          )}
          {historyState === 'ok' && historyWindow.recent.length === 0 && historyWindow.earlier.length > 0 && !showEarlierHistory && (
            <Typography color="text.secondary" sx={{ mb: 1 }} data-testid="alert-history-earlier-empty">
              No alerts from today. {historyWindow.earlier.length} earlier persisted alerts are available.
            </Typography>
          )}
          {historyList.length > 0 && (
            <TableContainer>
              <Table size="small" data-testid="alert-history-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Camera / area</TableCell>
                    <TableCell>Kind</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Delivery</TableCell>
                    <TableCell>Pattern</TableCell>
                    <TableCell>Investigate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyList.flatMap((item) => {
                    if (item.kind === 'row') {
                      return [renderHistoryRow(item.row, navigate, setSelectedCompositeId)];
                    }
                    const latest = item.rows[0];
                    const expanded = Boolean(expandedHistoryKeys[item.key]);
                    const summary = (
                      <TableRow key={item.key} hover data-testid="alert-history-group">
                        <TableCell>
                          <Typography variant="caption">{latest.timeLabel}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{latest.conditionLabel}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{latest.cameraAreaLabel}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={latest.alertClass}
                            size="small"
                            color={historyClassColor(latest.alertClass)}
                            variant="outlined"
                            data-testid="alert-history-alert-class"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{latest.severity}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {latest.status}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" data-testid="alert-history-delivery">
                            {latest.deliveryLabel}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {latest.patternIdentity || latest.patternMembershipSummary || (latest.alertClass === 'Pattern' ? 'Pattern' : '—')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => setExpandedHistoryKeys((prev) => ({ ...prev, [item.key]: !expanded }))}
                            data-testid="alert-history-show-repeats"
                          >
                            {expanded ? 'Hide repeats' : `Show ${item.rows.length} more`}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => navigate(buildScopedIncidentsPath(latest.investigateHref))}
                            data-testid="alert-history-investigate"
                          >
                            Investigate
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                    if (!expanded) return [summary];
                    return [summary, ...item.rows.map((row) => renderHistoryRow(row, navigate, setSelectedCompositeId))];
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {historyWindow.earlier.length > 0 && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setShowEarlierHistory((prev) => !prev)}
              data-testid="alert-history-show-earlier"
            >
              {showEarlierHistory
                ? 'Show today only'
                : `Show earlier alerts (${historyWindow.earlier.length})`}
            </Button>
          )}
        </Paper>

        {selectedCompositeDetail ? (
          <CompositeAlertDetail
            detail={selectedCompositeDetail}
            onClose={() => setSelectedCompositeId(null)}
          />
        ) : selectedReviewedComposite ? (
          <ReviewedCompositePatternDetail
            detail={selectedReviewedComposite}
            onClose={() => setSelectedCompositeId(null)}
          />
        ) : null}

        {/* System Status - Compact footer */}
        <Paper sx={{ p: 2, mt: 2, flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" fontWeight="bold">System Status:</Typography>
              <Chip
                label={wsConnected ? 'WebSocket Connected' : 'WebSocket Disconnected'}
                color={wsConnected ? 'success' : 'error'}
                size="small"
              />
              <Typography variant="body2" color="textSecondary">
                Last refresh: {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : '—'}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {activeCardViews.filter((card) => !card.acknowledged).length} unacknowledged alerts
            </Typography>
          </Box>
        </Paper>
      </Box>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
    </Box>
  );
};

export default LiveMonitor;
